// src/pages/Notices.jsx
// お知らせ（/notices。学生）【2026-10-16】
//   授業のお知らせ（先生から）と、大学からのお知らせ（教務から）の2つのタブ。
//   未読を上に。開いたものだけ既読にする（「すべて既読にする」は置かない）。返事はできない。
import React, { useCallback, useEffect, useState } from 'react';
import { msgApi } from '../messaging/api';
import { dateTime, useFeatures, useRealtime } from '../messaging/util';
import { AuthImage, FileChip } from '../messaging/ui';
import '../messaging/messaging.css';

/** 未読のバッジ（useUnreadCount）に知らせる */
function changed() {
  try { window.dispatchEvent(new Event('messages-changed')); } catch (_) { /* noop */ }
}

function sortUnreadFirst(list) {
  return [...list].sort((a, b) => (a.read === b.read ? b.id - a.id : a.read ? 1 : -1));
}

function useInbox(path, key, enabled) {
  const [items, setItems] = useState(null);
  const [more, setMore] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    if (!enabled) { setItems([]); return; }
    const r = await msgApi.get(path);
    if (!r.success) { setItems([]); setErr(r.message || '読み込めませんでした'); return; }
    setErr('');
    setItems(prev => {
      // さらに読み込んだ古い分は残す
      const fresh = r[key];
      if (!prev) return fresh;
      const ids = new Set(fresh.map(x => x.id));
      const minFresh = fresh.length ? fresh[fresh.length - 1].id : Infinity;
      return [...fresh, ...prev.filter(x => !ids.has(x.id) && x.id < minFresh)];
    });
    setMore(r[key].length >= 50);
  }, [path, key, enabled]);
  const loadMore = useCallback(async (oldestId) => {
    const r = await msgApi.get(`${path}?before_id=${oldestId}`);
    if (!r.success) return;
    setItems(prev => [...(prev || []), ...r[key].filter(x => !(prev || []).some(p => p.id === x.id))]);
    setMore(r[key].length >= 50);
  }, [path, key]);
  return { items, setItems, more, err, load, loadMore };
}

function NoticeCard({ title, sub, text, image, file, edited, read, open, onOpen }) {
  return (
    <div className={'card msg-notice' + (read ? '' : ' unread')}>
      <button type="button" className="msg-notice-head" onClick={onOpen} aria-expanded={open}>
        <span className="msg-notice-title">{!read && <span className="msg-unread-dot" aria-label="未読" />}{title}</span>
        <span className="msg-notice-sub">{sub}{edited && <>　編集済み</>}</span>
        {!open && text && <span className="msg-notice-excerpt">{text.length > 80 ? text.slice(0, 80) + '…' : text}</span>}
      </button>
      {open && (
        <div className="msg-notice-body">
          {text && <div className="msg-note-text">{text}</div>}
          {image && <AuthImage url={image.url} className="msg-notice-img" />}
          {file && <FileChip file={file} />}
        </div>
      )}
    </div>
  );
}

export default function Notices({ user }) {
  const { features, loaded } = useFeatures();
  const isStudent = user && user.role === 'student';
  const dev = !!(user && user.isDeveloper);
  const annOn = loaded && (features.announcements || dev);
  const bulOn = loaded && (features.bulletins || dev);
  const [tab, setTab] = useState('class');
  const [openId, setOpenId] = useState(null);

  const ann = useInbox('/announcements', 'announcements', isStudent && annOn);
  const bul = useInbox('/bulletins/inbox', 'bulletins', isStudent && bulOn);
  const { load: loadAnn } = ann;
  const { load: loadBul } = bul;

  useEffect(() => { if (loaded) { loadAnn(); loadBul(); } }, [loaded, loadAnn, loadBul]);
  useEffect(() => {
    if (loaded && !annOn && bulOn) setTab('univ');
  }, [loaded, annOn, bulOn]);
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') { loadAnn(); loadBul(); } }, 60000);
    return () => clearInterval(t);
  }, [loadAnn, loadBul]);
  useRealtime((ev) => { if (ev.type === 'bulletin') loadBul(); if (ev.type === 'announcement') loadAnn(); });

  const openAnn = (a) => {
    const key = 'a' + a.id;
    setOpenId(o => (o === key ? null : key));
    if (!a.read) {
      ann.setItems(list => list.map(x => (x.id === a.id ? { ...x, read: true } : x)));
      msgApi.post(`/announcements/${a.id}/read`).then(changed);
    }
  };
  const openBul = (b) => {
    const key = 'b' + b.id;
    setOpenId(o => (o === key ? null : key));
    if (!b.read) {
      bul.setItems(list => list.map(x => (x.id === b.id ? { ...x, read: true } : x)));
      msgApi.post(`/bulletins/${b.id}/read`).then(changed);
    }
  };

  if (!isStudent) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>お知らせ</h1></div>
        <div className="card"><p className="msg-help">このページは学生向けです。</p></div>
      </div>
    );
  }

  const annUnread = (ann.items || []).filter(x => !x.read).length;
  const bulUnread = (bul.items || []).filter(x => !x.read).length;
  const list = tab === 'class' ? ann : bul;
  const sorted = list.items ? sortUnreadFirst(list.items) : null;

  return (
    <div className="page-container msg-notices-page">
      <div className="page-header">
        <h1>お知らせ</h1>
        <p>授業の先生と大学からのお知らせです。返事はできません。質問は「メッセージ」から先生へどうぞ。</p>
      </div>
      {loaded && !annOn && !bulOn ? (
        <div className="card"><p className="msg-help">今はお知らせの機能を使えません。</p></div>
      ) : (
        <>
          <div className="tab-bar">
            {annOn && (
              <button type="button" className={'tab-btn' + (tab === 'class' ? ' active' : '')} onClick={() => setTab('class')}>
                授業のお知らせ{annUnread > 0 && <span className="msg-badge inline">{annUnread}</span>}
              </button>
            )}
            {bulOn && (
              <button type="button" className={'tab-btn' + (tab === 'univ' ? ' active' : '')} onClick={() => setTab('univ')}>
                大学からのお知らせ{bulUnread > 0 && <span className="msg-badge inline">{bulUnread}</span>}
              </button>
            )}
          </div>
          {list.err && <div className="card"><p className="msg-error">{list.err}</p></div>}
          {!loaded || sorted === null ? <div className="card"><p>読み込み中...</p></div>
            : sorted.length === 0 ? <div className="card"><p className="msg-help" style={{ textAlign: 'center', padding: '1.5rem' }}>お知らせはありません</p></div>
              : tab === 'class' ? sorted.map(a => (
                <NoticeCard key={'a' + a.id} title={a.class ? a.class.name : '授業'} sub={dateTime(a.sent_at)}
                            text={a.text} image={a.image} file={a.file} edited={a.edited} read={a.read}
                            open={openId === 'a' + a.id} onOpen={() => openAnn(a)} />
              )) : sorted.map(b => (
                <NoticeCard key={'b' + b.id} title={b.title} sub={dateTime(b.sent_at)}
                            text={b.text} edited={b.edited} read={b.read}
                            open={openId === 'b' + b.id} onOpen={() => openBul(b)} />
              ))}
          {list.more && sorted && sorted.length > 0 && (
            <div style={{ textAlign: 'center' }}>
              <button type="button" className="btn-secondary"
                      onClick={() => list.loadMore(Math.min(...list.items.map(x => x.id)))}>さらに読み込む</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
